import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchSeedVarietiesAdmin, getToken, updateSeedVarietyVariant, uploadFile } from './api';
import { AdminImagePreview, fileInput, input, label } from './adminUi';
import VarietySpecsFields from './VarietySpecsFields';

export default function SeedVarietyVariantEdit() {
  const { productId: productIdParam, variantId: variantIdParam } = useParams();
  const navigate = useNavigate();
  const decodedProductId = productIdParam ? decodeURIComponent(productIdParam) : '';
  const decodedVariantId = variantIdParam ? decodeURIComponent(variantIdParam) : '';
  const [name, setName] = useState('');
  const [breeder, setBreeder] = useState('');
  const [image, setImage] = useState('');
  const [alt, setAlt] = useState('');
  const [specs, setSpecs] = useState([{ label: '', value: '' }]);
  const [loadState, setLoadState] = useState('loading');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    let cancelled = false;
    fetchSeedVarietiesAdmin()
      .then((block) => {
        if (cancelled) return;
        const product = block?.families?.find((f) => f.id === decodedProductId);
        const v = product?.variants?.find((x) => x.id === decodedVariantId);
        if (!v) {
          setLoadState('notfound');
          return;
        }
        setName(v.name || '');
        setBreeder(v.breeder || '');
        setImage(v.image || '');
        setAlt(v.alt || '');
        setSpecs(Array.isArray(v.specs) && v.specs.length ? v.specs : [{ label: '', value: '' }]);
        setLoadState('ready');
      })
      .catch(() => {
        if (!cancelled) setLoadState('notfound');
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, decodedProductId, decodedVariantId]);

  async function onUpload(file) {
    if (!file) return;
    try {
      const { url } = await uploadFile(file);
      setImage(url);
    } catch (e) {
      setErr(e.message || 'Upload failed');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    setSaving(true);
    try {
      await updateSeedVarietyVariant(decodedProductId, decodedVariantId, {
        name,
        breeder,
        image,
        alt,
        specs: specs.filter((r) => r.label?.trim() || r.value?.trim()),
      });
      navigate(`/admin/dashboard/seed-varieties/products/${encodeURIComponent(decodedProductId)}/edit`);
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loadState === 'loading') {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-neutral-600">Loading…</div>
    );
  }

  if (loadState === 'notfound') {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-8 md:px-8">
        <p className="text-neutral-700">Variant not found.</p>
        <Link
          to={`/admin/dashboard/seed-varieties/products/${encodeURIComponent(decodedProductId)}/edit`}
          className="mt-4 inline-block text-sm font-semibold text-brand-lime"
        >
          ← Back
        </Link>
      </div>
    );
  }

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <Link
            to={`/admin/dashboard/seed-varieties/products/${encodeURIComponent(decodedProductId)}/edit`}
            className="text-xs font-medium text-brand-olive hover:underline"
          >
            ← Product
          </Link>
          <h2 className="mt-1 text-lg font-bold text-neutral-900 md:text-xl">Edit variant</h2>
          <p className="mt-0.5 font-mono text-xs text-neutral-500">id: {decodedVariantId}</p>
        </div>
      </header>

      <main className="min-h-0 w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}
        <form onSubmit={handleSubmit} className="w-full max-w-none rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <AdminImagePreview src={image} alt={alt} emptyLabel="Preview" />

          <div className="grid gap-4 sm:grid-cols-2">
            <label className={`${label} sm:col-span-2`}>
              Variety name
              <input className={input} value={name} onChange={(e) => setName(e.target.value)} required />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Breeder
              <input className={input} value={breeder} onChange={(e) => setBreeder(e.target.value)} />
            </label>
            <div className="sm:col-span-2 rounded-xl border border-neutral-200/90 bg-neutral-50/80 p-4">
              <p className="mb-2 text-xs font-semibold uppercase text-neutral-500">Image</p>
              <label className={`${label} mb-0`}>
                Upload
                <input type="file" accept="image/*" className={fileInput} onChange={(e) => onUpload(e.target.files?.[0])} />
              </label>
              <label className={`${label} mt-3`}>
                Image URL
                <input className={input} value={image} onChange={(e) => setImage(e.target.value)} />
              </label>
              <label className={`${label} mt-3`}>
                Alt text
                <input className={input} value={alt} onChange={(e) => setAlt(e.target.value)} />
              </label>
            </div>
          </div>

          <div className="mt-6">
            <VarietySpecsFields specs={specs} onChange={setSpecs} />
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-brand-forest px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save variant'}
            </button>
            <Link
              to={`/admin/dashboard/seed-varieties/products/${encodeURIComponent(decodedProductId)}/edit`}
              className="rounded-lg border border-neutral-300 bg-white px-5 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </>
  );
}
